import { IFiltersState, RoomOption } from '../types';
import { useLocale } from 'locale';
import { isNumber } from 'lodash';
import React from 'react';
import { isFilterActiveByType, normalizeRoomsRange } from '../utils';
import { DOT_SEPARATOR } from 'consts/symbols';
import { marketplaceSelector } from 'store/state/selectors/router';
import { useSelector } from 'react-redux';


type ValueOf<T> = T[keyof T];
export const filtersThumbnailsConfig: { [ K in keyof IFiltersState ]: React.ComponentType<{ value: IFiltersState[K], initialValue: ValueOf<IFiltersState>, isTimeLineB?: boolean }> } = {
  dealType: ({ value }) => {
    const { t } = useLocale();
    return <>{t('filters.dealType.saveCard', { dealType: value })}</>;
  },
  propertyTypes: ({ value }) => {
    const { t } = useLocale();
    return <>{value.map( v => t('filters.propertyTypeOptions', { value: v })).join(DOT_SEPARATOR)}</>;
  },
  seller: ({ value }) => {
    const { t } = useLocale();
    return <>{value.map( v => t('filters.sellerOptions', { value: v })).join(DOT_SEPARATOR)}</>;
  },
  conditions: ({ value }) => {
    const { t } = useLocale();
    return <>{value.map( v => t('filters.conditionsOptions', { value: v })).join(DOT_SEPARATOR)}</>;
  },
  amenities: ({ value }) => {
    const { t } = useLocale();
    return <>{value.map( v => t('filters.amenitiesOptions', { value: v })).join(DOT_SEPARATOR)}</>;
  },
  priceRange: ({ value: [ from, to ], initialValue: [ initialFrom, initialTo ] }) => {
    const { formatMoneyWithMetricPrefix, t } = useLocale();
    const priceFrom = from === initialFrom ? null : formatMoneyWithMetricPrefix(from);
    const priceTo = to === initialTo ? null : formatMoneyWithMetricPrefix(to);
    const isActive = priceFrom !== null || priceTo !== null;
    return <>{t('filters.priceShortValue', { isActive, priceFrom, priceTo })}</>;
  },
  ppmRange: ({ value: [ from, to ], initialValue: [ initialFrom, initialTo ] }) => {
    const { formatMoneyWithMetricPrefix, t } = useLocale();
    const priceFrom = from === initialFrom ? null : formatMoneyWithMetricPrefix(from);
    const priceTo = to === initialTo ? null : formatMoneyWithMetricPrefix(to);

    const isActive = priceFrom !== null || priceTo !== null;
    return <>{t('filters.ppmShortValue', { isActive, priceFrom, priceTo })}</>;
  },
  monthlyTaxRange: ({ value: [ from, to ], initialValue: [ initialFrom, initialTo ] }) => {
    const { formatMoneyWithMetricPrefix, t } = useLocale();
    const priceFrom = from === initialFrom ? null : formatMoneyWithMetricPrefix(from);
    const priceTo = to === initialTo ? null : formatMoneyWithMetricPrefix(to);

    const isActive = priceFrom !== null || priceTo !== null;
    return <>{t('filters.monthlyTaxShortValue', { isActive, priceFrom, priceTo })}</>;
  },
  // @ts-ignore
  roomsRange: ({ value, initialValue, isTimeLineB = false }) => {
    const { t } = useLocale();
    const marketplace = useSelector(marketplaceSelector);
    // tslint:disable-next-line:prefer-const
    let [ bedsFrom, bedsTo ] = value;

    if (bedsFrom === 0 && bedsTo === null) {
      bedsTo = 0;
    }

    if (isTimeLineB && ((bedsFrom === null && bedsTo === null) || (bedsFrom === initialValue[0] && bedsTo === initialValue[1]))) {
      return <>{t('filters.roomsShortValue.anyBeds')}</>;
    }

    if (isTimeLineB) {
      const normalizedCommitedValue = normalizeRoomsRange(value, marketplace);
      const isActive = isFilterActiveByType.roomsRange(value, initialValue as [RoomOption, RoomOption], marketplace);
      const [ nomalizedFrom, normalizedTo ] = normalizedCommitedValue;

      return t('filters.roomsShortValue', {
        isActive,
        exactValue: nomalizedFrom === normalizedTo ? nomalizedFrom : null,
        isRange: nomalizedFrom !== null && normalizedTo !== null,
        bedsFrom: nomalizedFrom,
        bedsTo: normalizedTo,
      });
    }

    return (
      <>
        {t('filters.roomsShortValue', {
          isActive: true,
          exactValue: isNumber(bedsFrom) && bedsFrom === bedsTo ? bedsFrom : null,
          isRange: isNumber(bedsFrom) && isNumber(bedsTo),
          bedsFrom,
          bedsTo,
        })}
      </>
    );
  },
  floorRange: ({ value }) => {
    const { t } = useLocale();
    // tslint:disable-next-line:prefer-const
    let [ floorsFrom, floorsTo ] = value;

    if (floorsFrom === 1 && floorsTo === null) {
      floorsTo = 1;
    }

    return (
      <>
        {t('filters.floorRangeShortValue', {
          isActive: true,
          exactValue: isNumber(floorsFrom) && floorsFrom === floorsTo ? floorsFrom : null,
          isRange: isNumber(floorsFrom) && isNumber(floorsTo),
          floorsFrom,
          floorsTo,
        })}
      </>
    );
  },
  areaRange: ({ value: [ areaFrom, areaTo ] }) => {
    const { t, formatArea } = useLocale();
    return (
      <>
        {t('filters.areaRangeShortValue', {
          isActive: true,
          exactValue: isNumber(areaFrom) && areaFrom === areaTo ? formatArea(areaFrom) : null,
          isRange: isNumber(areaFrom) && isNumber(areaTo),
          areaFrom: areaFrom ? formatArea(areaFrom) : areaFrom,
          areaTo: areaTo ? formatArea(areaTo) : areaTo,
        })}
      </>
    );
  },
  bathsRange: ({ value }) => {
    const { t } = useLocale();
    const [ bathsFrom, bathsTo ] = value;
    return (
      <>
        {t('filters.bathsShortValue', {
          isActive: true,
          exactValue: bathsFrom === bathsTo ? bathsFrom : null,
          isRange: bathsFrom !== null && bathsTo !== null,
          bathsFrom,
          bathsTo,
        })}
      </>
    );
  },
  fee: ({ value }) => {
    if (!value) return null;
    const { t } = useLocale();
    return (<>{t('filters.feeLabel')}</>);
  },
};
